import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api';

const EMPTY = { enabled: false, title: '', message: '', cta_text: '', cta_link: '/packages' };

export const OfferBannerForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/offer-banner').then((res) => setForm({ ...EMPTY, ...res.data })).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (form.enabled && !form.title.trim()) {
      toast.error('Add a title before turning the banner on');
      return;
    }
    setSaving(true);
    try {
      const res = await api.put('/admin/offer-banner', form);
      setForm({ ...EMPTY, ...res.data });
      toast.success(res.data.enabled ? 'Offer banner is live on the homepage' : 'Offer banner saved (hidden)');
    } catch (err) {
      toast.error('Failed to save banner');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="offer-banner-form">
      <Card className="border-0 shadow-sm lg:col-span-3">
        <CardContent className="p-6">
          <form onSubmit={save} className="space-y-5">
            <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50">
              <div>
                <p className="font-semibold text-gray-900">Show banner on homepage</p>
                <p className="text-sm text-gray-500">Visitors can dismiss it for their session</p>
              </div>
              <Switch data-testid="offer-enabled-switch" checked={form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} />
            </div>
            <div>
              <Label htmlFor="offer-title" className="mb-2 block">Title *</Label>
              <Input data-testid="offer-title-input" id="offer-title" value={form.title} onChange={set('title')} maxLength={80} placeholder="Summer Sale: 20% off Kerala packages" className="border-2" />
            </div>
            <div>
              <Label htmlFor="offer-message" className="mb-2 block">Message</Label>
              <Input data-testid="offer-message-input" id="offer-message" value={form.message} onChange={set('message')} maxLength={200} placeholder="Book before 30 June to lock in the deal." className="border-2" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="offer-cta" className="mb-2 block">Button text</Label>
                <Input data-testid="offer-cta-input" id="offer-cta" value={form.cta_text} onChange={set('cta_text')} maxLength={40} placeholder="View deals" className="border-2" />
              </div>
              <div>
                <Label htmlFor="offer-link" className="mb-2 block">Button link</Label>
                <Input data-testid="offer-link-input" id="offer-link" value={form.cta_link} onChange={set('cta_link')} maxLength={200} placeholder="/packages" className="border-2" />
              </div>
            </div>
            <Button data-testid="offer-save-btn" type="submit" disabled={saving} className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white">
              {saving ? 'Saving...' : 'Save banner'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <div className="lg:col-span-2">
        <p className="text-sm font-semibold text-gray-500 mb-2 uppercase tracking-wide">Preview</p>
        <div className={`rounded-xl overflow-hidden shadow ${form.enabled ? '' : 'opacity-50 grayscale'}`} data-testid="offer-preview">
          <div className="bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 text-gray-900 px-5 py-4 flex items-start gap-2">
            <Sparkles className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-bold">{form.title || 'Your offer title'}</p>
              {form.message && <p className="text-sm">{form.message}</p>}
              {form.cta_text && <span className="inline-block mt-2 bg-gray-900 text-white text-xs font-semibold px-3 py-1 rounded-full">{form.cta_text}</span>}
            </div>
          </div>
        </div>
        {!form.enabled && <p className="text-xs text-gray-400 mt-2">Banner is currently hidden from visitors.</p>}
      </div>
    </div>
  );
};
